'use client';

import { Suspense, lazy, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Dialog, type DialogProps } from '@packages/shared/src/ui/Dialog/Dialog';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { dispatchDisplayOverlayTracking } from '@packages/shared/src/ui/Dialog/dispatchDialogOverlayTracking';

import { DialogHeader } from '../Dialog/DialogHeader/DialogHeader.client';

const messages = defineMessages({
  modalHeadline: {
    id: 'LoginModal.modal.headline',
    defaultMessage: 'Schnell anmelden',
  },
});

// dynamic import to avoid loading the mybuy stuff on initial render
const LoginForm = lazy(() => import(/* webpackChunkName: 'LoginForm' */ './LoginForm.client'));

/** Dialog with user login form in two modes: 'e-mail & password login' or 'customer number & birthday login' */
export const LoginDialog = ({ onClose, isOpen, ...props }: DialogProps) => {
  const { formatMessage } = useIntl();
  const dispatchGTMEvent = useTracking();

  useEffect(() => {
    if (isOpen) {
      dispatchDisplayOverlayTracking({
        eventDispatcher: dispatchGTMEvent,
        displayTrackingProps: { category: 'Login', label: formatMessage(messages.modalHeadline) },
      });
    }
  }, [dispatchGTMEvent, formatMessage, isOpen]);

  return (
    <Dialog {...props} isOpen={isOpen} onClose={onClose} className="w-[600px] rounded-sm">
      <DialogHeader onClose={onClose}>{formatMessage(messages.modalHeadline)}</DialogHeader>
      <DialogContent className="bg-color-grey-light p-0">
        <Suspense>
          <LoginForm onLogin={() => onClose()} />
        </Suspense>
      </DialogContent>
    </Dialog>
  );
};
